import { useState, useRef, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../../Constants';

const SendTemplate = () => {
  // State for form data
  const [formData, setFormData] = useState({
    // message: '',
    templateName: ''
  });

  // State for files
  const [numbersFile, setNumbersFile] = useState(null);
  
  // State for loading and feedback
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // State for templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Refs for file inputs
  const numbersFileInputRef = useRef(null);
  
  const { user } = useContext(AuthContext);
  const token = user?.token || localStorage.getItem("token");
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!companyId) return;
      
      try {
        const response = await fetch(`${API_URL}/api/templates/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setTemplates(data);
        } else {
          console.error('Failed to fetch templates');
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, [companyId, token]);

  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    
    if (templateId) {
      const selected = templates.find(t => t.id === templateId);
      if (selected) {
        // Find the BODY component text
        const bodyComponent = selected.components.find(c => c.type === 'BODY');
        if (bodyComponent && bodyComponent.text) {
          setFormData(prev => ({
            ...prev,
            message: bodyComponent.text,
            templateName: selected.name
          }));
        }
      }
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle numbers file upload
  const handleNumbersFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setNumbersFile(uploadedFile);
    }
  };

  // Remove selected numbers file
  const removeNumbersFile = () => {
    setNumbersFile(null);
    if (numbersFileInputRef.current) {
      numbersFileInputRef.current.value = '';
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!numbersFile) {
      setError('Please upload a file containing phone numbers');
      return;
    }
    
    if (!formData.templateName) {
      setError('Please select a template');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const formDataToSend = new FormData();
      formDataToSend.append('companyId', companyId);
      formDataToSend.append('templateName', formData.templateName);
      formDataToSend.append('numbersFile', numbersFile);
      
      // Add message only if it's not empty
      // if (formData.message) {
      //   formDataToSend.append('message', formData.message);
      // }
      
      const response = await fetch(`${API_URL}/api/whatsappConfig/sendVerifiedTemplates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataToSend
      });
      
      if (response.ok) {
        const result = await response.json();
        setSuccess(true);
        console.log('Template messages sent successfully:', result);
        
        // Reset form after successful submission
        setFormData({
          message: '',
          templateName: ''
        });
        setNumbersFile(null);
        setSelectedTemplate('');
        if (numbersFileInputRef.current) numbersFileInputRef.current.value = '';
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send template messages');
      }
    } catch (error) {
      console.error('Error sending template messages:', error);
      setError(error.message || 'Failed to send template messages');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">WhatsApp Template Marketing Tool</h1>
          <p className="text-md text-gray-600 max-w-2xl mx-auto">
            Send verified templates to multiple contacts using a file upload
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100">
            <div className="px-5 py-5">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-1.5 rounded-md">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <h2 className="ml-3 text-xl font-bold text-gray-800">Send Verified Template</h2>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Numbers File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Numbers File (TXT, CSV, XLS, XLSX)
                  </label>
                  <div className="mt-1 flex flex-col">
                    <input
                      ref={numbersFileInputRef}
                      type="file"
                      id="numbersFile"
                      name="numbersFile"
                      accept=".txt,.csv,.xls,.xlsx"
                      className="hidden"
                      onChange={handleNumbersFileUpload}
                      required
                    />
                    <label
                      htmlFor="numbersFile"
                      className="cursor-pointer rounded-lg border border-gray-300 bg-white py-2 px-3 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center justify-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Upload Numbers File
                    </label>
                    {numbersFile && (
                      <div className="mt-2 flex items-center justify-between bg-gray-100 p-2 rounded-md">
                        <span className="text-xs text-gray-700 truncate">{numbersFile.name}</span>
                        <button
                          type="button"
                          onClick={removeNumbersFile}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Template Dropdown */}
                <div>
                  <label htmlFor='template' className='block text-sm font-medium text-gray-700 mb-1'>
                    Template *
                  </label>
                  <select
                    id="template"
                    name="template"
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                    required
                  >
                    <option value="">Select a template</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-3 border"
                    placeholder="message ..."
                    value={formData.message}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Status Messages */}
                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                  </div>
                )}
                
                {success && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">Template messages sent successfully!</span>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex w-full justify-center rounded-lg border border-transparent py-2 px-4 text-sm font-medium text-white shadow-md transition-all duration-200 ${
                      loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg className="w-4 h-4 mr-1 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        </svg>
                        Send Template Messages
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100">
            <div className="px-5 py-5">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="ml-3 text-xl font-bold text-gray-800">Template Preview</h2>
              </div>
              
              <div className="flex justify-center">
                <div className="w-64 bg-gray-900 p-4 rounded-2xl shadow-xl border-2 border-gray-800">
                  {/* Phone status bar */}
                  <div className="flex justify-between items-center mb-3 px-1">
                    <span className="text-xs text-white font-medium">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <div className="flex items-center">
                      <svg className="w-3 h-3 text-white mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 01.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.242 0 1 1 0 01-1.415-1.415 5 5 0 017.072 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      <svg className="w-3 h-3 text-white mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 4.5A2.5 2.5 0 014.5 2h11A2.5 2.5 0 0118 4.5v11a2.5 2.5 0 01-2.5 2.5h-11A2.5 2.5 0 012 15.5v-11zM4.5 4a.5.5 0 00-.5.5v11a.5.5 0 00.5.5h11a.5.5 0 00.5-.5v-11a.5.5 0 00-.5-.5h-11z" />
                      </svg>
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>

                  {/* WhatsApp header */}
                  <div className="flex justify-between items-center mb-3 p-2 bg-green-700 rounded-xl">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-2">
                        <p className="text-xs font-medium text-white">WhatsApp Business</p>
                        <p className="text-xs text-green-200">Online</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                      </svg>
                    </div>
                  </div>

                  {/* Template message preview */}
                  <div className="bg-gray-800 p-3 rounded-b-xl min-h-[300px]">
                    <div className="space-y-2">
                      {formData.templateName && (
                        <div className="bg-green-700 p-2 rounded-md ml-3">
                          <p className="text-xs text-green-200 font-medium mb-1">Template: {formData.templateName}</p>
                          <p className="text-xs text-white whitespace-pre-line">{formData.message}</p>
                          <p className="text-xs text-green-200 text-right mt-1">
                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      )}
                      
                      {!formData.templateName && (
                        <div className="text-center py-10">
                          <svg className="w-12 h-12 text-gray-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-xs text-gray-400">Select a template to see preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendTemplate;